import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Autocomplete,
  Tooltip,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  PlusIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import useActionParam from '../../hooks/useActionParam';
import { useFilterParams } from '../../hooks/useFilterParams';
import { useTheme } from '../../context/ThemeContext';
import { financeApi } from '../../api/financeApi';
import { formatCurrency, CURRENCY } from '../../config/currency';
import Modal from '../../components/Modal';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import SupplierHistoryModal from '../../components/finance/SupplierHistoryModal';
import ExpenseForm, { ExpenseFormData, ExpenseCategory as ExpenseFormCategory } from '../../components/finance/ExpenseForm';
import PaymentModeSelect from '../../components/finance/PaymentModeSelect';
import { todayLocalIso } from '../../utils/dateLocal';
import {
  AgingBucket,
  PeriodPreset,
  expenseAgingBucket,
  expenseFullyInside,
  expenseOverlapsWindow,
  resolvePeriodWindow,
} from '../../utils/payablesFilters';
import PayablesCharts from '../../components/finance/PayablesCharts';
import FinanceDateRange from '../../components/finance/FinanceDateRange';
import FilteredTotals from '../../components/finance/FilteredTotals';
import { fuzzyFilter } from '../../utils/fuzzySearch';
import {
  PrimaryButton,
  SecondaryButton,
  SuccessButton,
  PageHeroBand,
  EmptyState,
  CardContainer,
  StatusChip,
  AppIconButton,
} from '../../components/ui';

// --- Types ---

interface PayableExpense {
  id: number;
  date: string;
  fromDate?: string | null;
  toDate?: string | null;
  amount: number;
  dueDate: string | null;
  categoryName: string;
  paymentStatus: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID';
  amountPaid: number;
  description: string | null;
}

interface SupplierPayable {
  supplierName: string;
  totalExpenses: number;
  totalPaid: number;
  outstanding: number;
  overdue: boolean;
  expenses: PayableExpense[];
}

interface PayablesResponse {
  totalOutstanding: number;
  overdueAmount: number;
  suppliers: SupplierPayable[];
}

type SortMode = 'outstanding_desc' | 'due_asc';
type PayablesView = 'outstanding' | 'completed';

// --- Component ---

const SupplierPayables: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  // Data state
  const [payablesData, setPayablesData] = useState<PayablesResponse>({
    totalOutstanding: 0,
    overdueAmount: 0,
    suppliers: [],
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const handleOpenBulkPayRef = React.useRef<() => void>();
  // Deep-link: dashboard Quick Action → open bulk record-payment (only when payable).
  const [refreshing, setRefreshing] = useState(false);
  const [expandedSupplier, setExpandedSupplier] = useState<string | null>(null);
  const [historySupplier, setHistorySupplier] = useState<string | null>(null);
  const [payablesFilters, setPayablesFilters, resetPayablesFilters] = useFilterParams(
    {
      sortMode: 'outstanding_desc',
      view: 'outstanding',
      q: '',
      aging: 'all',
      categories: '', // CSV of category names
      period: 'all',
      from: '',
      to: '',
      charts: 'off',
      fx: 'off', // filters expanded
    },
    { prefix: 'pay_' },
  );
  const sortMode = payablesFilters.sortMode as SortMode;
  const view = payablesFilters.view as PayablesView;
  useActionParam(
    'pay',
    !loading && view === 'outstanding' && payablesData.suppliers.length > 0,
    () => handleOpenBulkPayRef.current?.(),
  );
  const q = payablesFilters.q as string;
  const aging = payablesFilters.aging as 'all' | AgingBucket;
  const categoriesCsv = payablesFilters.categories as string;
  const period = payablesFilters.period as PeriodPreset;
  const customFrom = payablesFilters.from as string;
  const customTo = payablesFilters.to as string;
  const chartsVisible = (payablesFilters.charts as string) !== 'off';
  const filtersExpanded = (payablesFilters.fx as string) === 'on';
  const selectedCategories = useMemo<string[]>(
    () => (categoriesCsv ? categoriesCsv.split(',').filter(Boolean) : []),
    [categoriesCsv],
  );

  const setSortMode = useCallback(
    (v: SortMode) => setPayablesFilters({ sortMode: v }),
    [setPayablesFilters],
  );
  const setView = useCallback(
    (v: PayablesView) => setPayablesFilters({ view: v }),
    [setPayablesFilters],
  );
  const setQ = useCallback((v: string) => setPayablesFilters({ q: v }), [setPayablesFilters]);
  const setAging = useCallback(
    (v: 'all' | AgingBucket) => setPayablesFilters({ aging: v }),
    [setPayablesFilters],
  );
  const setSelectedCategories = useCallback(
    (vals: string[]) => setPayablesFilters({ categories: vals.join(',') }),
    [setPayablesFilters],
  );
  const setPeriod = useCallback(
    (v: PeriodPreset) => setPayablesFilters({ period: v }),
    [setPayablesFilters],
  );
  const setCustomRange = useCallback(
    (from: string, to: string) => setPayablesFilters({ from, to }),
    [setPayablesFilters],
  );
  const setChartsVisible = useCallback(
    (on: boolean) => setPayablesFilters({ charts: on ? 'on' : 'off' }),
    [setPayablesFilters],
  );
  const setFiltersExpanded = useCallback(
    (on: boolean) => setPayablesFilters({ fx: on ? 'on' : 'off' }),
    [setPayablesFilters],
  );

  // Count of non-default filters currently applied (drives badge on Filters chip).
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (aging !== 'all') n += 1;
    if (selectedCategories.length > 0) n += 1;
    if (period !== 'all') n += 1;
    return n;
  }, [aging, selectedCategories, period]);

  // Auto-expand filter panel on first render if a non-search filter is already active
  // from the URL — so the user can see what's set without hunting for the chip.
  useEffect(() => {
    if (activeFilterCount > 0 && !filtersExpanded) setFiltersExpanded(true);
    // intentional: run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Payment modal state
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payingExpense, setPayingExpense] = useState<PayableExpense | null>(null);
  const [payingSupplier, setPayingSupplier] = useState<string>('');
  const [payAmount, setPayAmount] = useState<string>('');
  const [payMode, setPayMode] = useState('CASH');
  const [payNotes, setPayNotes] = useState('');
  const [payDate, setPayDate] = useState(todayLocalIso());
  const [saving, setSaving] = useState(false);

  // Add Payable modal state
  const [addPayableOpen, setAddPayableOpen] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [addPayable, setAddPayable] = useState({
    supplierName: '',
    amount: '',
    fromDate: todayLocalIso(),
    toDate: '',
    dueDate: '',
    categoryId: '' as number | '',
    description: '',
  });
  const [savingPayable, setSavingPayable] = useState(false);

  // Edit payable (Expense edit) state
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseFormData | null>(null);
  const [editFormCategories, setEditFormCategories] = useState<ExpenseFormCategory[]>([]);

  // Bulk record-payment (page-level) state. Auto-allocates oldest-due-first across
  // the chosen supplier's outstanding payables.
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [bulkSupplier, setBulkSupplier] = useState<string>('');
  const [bulkAmount, setBulkAmount] = useState<string>('');
  const [bulkMode, setBulkMode] = useState<string>('CASH');
  const [bulkDate, setBulkDate] = useState<string>(todayLocalIso());
  const [bulkNotes, setBulkNotes] = useState<string>('');
  const [bulkSaving, setBulkSaving] = useState(false);

  // --- Data Fetching ---

  const fetchPayables = useCallback(async () => {
    try {
      const res = await financeApi.getPayables(view);
      const d = res.data;
      // Backend returns array of supplier objects directly, not a wrapper
      const suppliers = Array.isArray(d) ? d : (d?.suppliers ?? []);
      const totalOutstanding = suppliers.reduce((sum: number, s: Record<string, number>) => sum + (s.outstanding || 0), 0);
      const overdueAmount = suppliers.reduce((sum: number, s: Record<string, number>) => sum + (s.overdueAmount || 0), 0);
      setPayablesData({
        totalOutstanding,
        overdueAmount,
        suppliers,
      });
    } catch (err: unknown) {
      console.error('Error fetching payables:', err);
      toast.error('Failed to load payables');
    }
  }, [view]);

  // Re-fetch when toggling outstanding ↔ completed.
  useEffect(() => {
    setLoading(true);
    fetchPayables().finally(() => setLoading(false));
  }, [view, fetchPayables]);

  // Load expense categories once for the Category filter dropdown.
  useEffect(() => {
    void fetchExpenseCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSupplierOptions = useCallback(async () => {
    try {
      const res = await financeApi.getUniqueSuppliers();
      setSupplierOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error('Error fetching suppliers:', err);
    }
  }, []);

  const fetchExpenseCategories = useCallback(async () => {
    try {
      const res = await financeApi.getCategories();
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setExpenseCategories(data);
    } catch (err: unknown) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPayables();
    setRefreshing(false);
  };

  // --- Filter + Sort pipeline ---

  const periodWindow = useMemo(
    () => resolvePeriodWindow(period, customFrom, customTo),
    [period, customFrom, customTo],
  );

  // Any filter active = aggregates recompute from visibleExpenses (otherwise lifetime).
  const filtersActive = useMemo(
    () =>
      q.trim() !== '' ||
      aging !== 'all' ||
      selectedCategories.length > 0 ||
      period !== 'all',
    [q, aging, selectedCategories, period],
  );

  // Apply expense-level filters (category / aging / period) per supplier.
  // Then drop suppliers with no surviving expenses and apply supplier-name search.
  const fuzzyMatchedSupplierNames = useMemo(() => {
    const query = q.trim();
    if (!query) return null;
    const matches = fuzzyFilter(
      payablesData.suppliers,
      ['supplierName'],
      query,
      { threshold: 0.4 },
    );
    return new Set(matches.map((s) => s.supplierName));
  }, [payablesData.suppliers, q]);

  const filteredSuppliers = useMemo(() => {
    const today = new Date();
    const catSet = new Set(selectedCategories);
    return payablesData.suppliers
      .map((s) => {
        const visibleExpenses = s.expenses.filter((e) => {
          if (catSet.size > 0 && !catSet.has(e.categoryName)) return false;
          if (aging !== 'all' && expenseAgingBucket(e, today) !== aging) return false;
          if (periodWindow && !expenseOverlapsWindow(e, periodWindow.from, periodWindow.to)) return false;
          return true;
        });
        if (fuzzyMatchedSupplierNames && !fuzzyMatchedSupplierNames.has(s.supplierName)) {
          return null;
        }
        if (visibleExpenses.length === 0 && filtersActive) return null;

        // Recompute aggregates from visibleExpenses when filters are active.
        if (filtersActive) {
          const totalExpenses = visibleExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
          const totalPaid = visibleExpenses.reduce((sum, e) => sum + (e.amountPaid || 0), 0);
          const outstanding = Math.max(totalExpenses - totalPaid, 0);
          return {
            ...s,
            expenses: visibleExpenses,
            totalExpenses,
            totalPaid,
            outstanding,
          } as SupplierPayable;
        }
        return s;
      })
      .filter((s): s is SupplierPayable => s !== null);
  }, [payablesData.suppliers, fuzzyMatchedSupplierNames, aging, selectedCategories, periodWindow, filtersActive]);

  const sortedSuppliers = useMemo(() => {
    const arr = [...filteredSuppliers];
    arr.sort((a, b) => {
      if (sortMode === 'outstanding_desc') return b.outstanding - a.outstanding;
      // due_asc: overdue first, then by earliest due date in expenses
      const aOverdue = a.overdue ? 0 : 1;
      const bOverdue = b.overdue ? 0 : 1;
      return aOverdue - bOverdue || a.supplierName.localeCompare(b.supplierName);
    });
    return arr;
  }, [filteredSuppliers, sortMode]);

  // Filtered totals for the summary cards (only used when filtersActive).
  const filteredTotalOutstanding = useMemo(
    () => filteredSuppliers.reduce((sum, s) => sum + (s.outstanding || 0), 0),
    [filteredSuppliers],
  );
  const filteredOverdueAmount = useMemo(() => {
    const today = new Date();
    return filteredSuppliers.reduce((sum, s) => {
      const overdueSum = s.expenses
        .filter((e) => expenseAgingBucket(e, today) !== 'current')
        .reduce((acc, e) => acc + Math.max((e.amount || 0) - (e.amountPaid || 0), 0), 0);
      return sum + overdueSum;
    }, 0);
  }, [filteredSuppliers]);

  const clearAllFilters = useCallback(() => {
    setPayablesFilters({ q: '', aging: 'all', categories: '', period: 'all', from: '', to: '' });
  }, [setPayablesFilters]);

  // Suppress unused-import lint when reset helper isn't wired (kept for future).
  void resetPayablesFilters;

  // --- Payment Modal ---

  const handleOpenPayModal = (expense: PayableExpense, supplierName: string) => {
    setPayingExpense(expense);
    setPayingSupplier(supplierName);
    setPayAmount(String(expense.amount - (expense.amountPaid || 0)));
    setPayMode('CASH');
    setPayNotes('');
    setPayDate(todayLocalIso());
    setPayModalOpen(true);
  };

  const handleClosePayModal = () => {
    setPayModalOpen(false);
    setPayingExpense(null);
    setPayingSupplier('');
  };

  const handleRecordPayment = async () => {
    if (!payingExpense) return;
    const amount = Number(payAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid payment amount');
      return;
    }

    setSaving(true);
    try {
      await financeApi.recordPayment({
        expenseId: payingExpense.id,
        amount,
        paymentMode: payMode,
        date: payDate,
        notes: payNotes.trim() || null,
      });
      toast.success(`Payment of ${formatCurrency(amount)} recorded for ${payingSupplier}`);
      handleClosePayModal();
      await fetchPayables();
    } catch (err: unknown) {
      console.error('Error recording payment:', err);
      toast.error('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  // --- Bulk Record Payment (page-level, auto-allocate oldest-first) ---

  const handleOpenBulkPay = (preselectSupplier?: string) => {
    setBulkSupplier(preselectSupplier || '');
    setBulkAmount('');
    setBulkMode('CASH');
    setBulkDate(todayLocalIso());
    setBulkNotes('');
    setBulkPayOpen(true);
  };
  handleOpenBulkPayRef.current = handleOpenBulkPay;

  const handleCloseBulkPay = () => {
    setBulkPayOpen(false);
    setBulkSupplier('');
    setBulkAmount('');
  };

  // Preview: which outstanding payables this amount would settle (oldest-due-first).
  // Matches backend allocation logic so vendor sees the same result before confirming.
  const bulkPreview = (() => {
    if (!bulkSupplier || !bulkAmount) return null;
    const amount = Number(bulkAmount);
    if (!amount || amount <= 0) return null;
    const supplier = payablesData.suppliers.find((s) => s.supplierName === bulkSupplier);
    if (!supplier) return null;
    const ordered = [...supplier.expenses].sort((a, b) => {
      const ad = a.dueDate || a.date;
      const bd = b.dueDate || b.date;
      return ad.localeCompare(bd);
    });
    let remaining = amount;
    const allocations: Array<{ exp: PayableExpense; applied: number; fullySettled: boolean }> = [];
    for (const exp of ordered) {
      if (remaining <= 0) break;
      const owed = exp.amount - exp.amountPaid;
      if (owed <= 0) continue;
      const applied = Math.min(remaining, owed);
      allocations.push({ exp, applied, fullySettled: applied >= owed });
      remaining -= applied;
    }
    return { allocations, remaining, totalOutstanding: supplier.outstanding };
  })();

  const handleBulkPaySubmit = async () => {
    const amount = Number(bulkAmount);
    if (!bulkSupplier) {
      toast.error('Pick a supplier');
      return;
    }
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setBulkSaving(true);
    try {
      const res = await financeApi.recordBulkPayment({
        supplierName: bulkSupplier,
        amount,
        paymentMode: bulkMode,
        paymentDate: bulkDate,
        notes: bulkNotes.trim() || null,
      });
      const created = (res.data?.paymentsCreated ?? 0) as number;
      const allocated = Number(res.data?.totalAllocated ?? 0);
      const remaining = Number(res.data?.remaining ?? 0);
      if (remaining > 0) {
        toast.success(
          `Paid ${formatCurrency(allocated)} across ${created} payables. ${formatCurrency(remaining)} unallocated (no more outstanding).`
        );
      } else {
        toast.success(`Paid ${formatCurrency(allocated)} across ${created} payable${created === 1 ? '' : 's'}`);
      }
      handleCloseBulkPay();
      await fetchPayables();
    } catch (err: unknown) {
      console.error('Error recording bulk payment:', err);
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to record payment';
      toast.error(message);
    } finally {
      setBulkSaving(false);
    }
  };

  // --- Add Payable ---

  const handleOpenAddPayable = async () => {
    setAddPayable({
      supplierName: '',
      amount: '',
      fromDate: todayLocalIso(),
      toDate: '',
      dueDate: '',
      categoryId: '',
      description: '',
    });
    setAddPayableOpen(true);
    // Fetch options in parallel
    await Promise.all([fetchSupplierOptions(), fetchExpenseCategories()]);
  };

  const handleSavePayable = async () => {
    if (!addPayable.supplierName.trim()) {
      toast.error('Supplier name is required');
      return;
    }
    if (!addPayable.amount || Number(addPayable.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!addPayable.fromDate) {
      toast.error('From date is required');
      return;
    }
    if (addPayable.toDate && addPayable.toDate < addPayable.fromDate) {
      toast.error('To date must be on or after From date');
      return;
    }
    if (!addPayable.dueDate) {
      toast.error('Due date is required');
      return;
    }
    if (!addPayable.categoryId) {
      toast.error('Category is required');
      return;
    }

    setSavingPayable(true);
    try {
      await financeApi.createExpense({
        supplierName: addPayable.supplierName.trim(),
        amount: Number(addPayable.amount),
        date: addPayable.fromDate,
        fromDate: addPayable.fromDate,
        toDate: addPayable.toDate || null,
        dueDate: addPayable.dueDate,
        categoryId: addPayable.categoryId,
        description: addPayable.description.trim() || null,
        paymentStatus: 'UNPAID',
        paymentMode: 'CREDIT',
      });
      toast.success(`Payable added for ${addPayable.supplierName.trim()}`);
      setAddPayableOpen(false);
      await fetchPayables();
    } catch (err: unknown) {
      console.error('Error creating payable:', err);
      toast.error('Failed to add payable');
    } finally {
      setSavingPayable(false);
    }
  };

  // --- Edit Payable (reuses ExpenseForm) ---

  const handleOpenEditPayable = async (expenseId: number) => {
    try {
      const [expRes, catsRes] = await Promise.all([
        financeApi.getExpense(expenseId),
        financeApi.getCategories(),
      ]);
      const exp = expRes.data;
      const cats = Array.isArray(catsRes.data) ? catsRes.data : (catsRes.data?.content || []);
      setEditFormCategories(cats);
      setEditingExpense({
        id: exp.id,
        categoryId: exp.categoryId ?? exp.category?.id ?? '',
        amount: exp.amount,
        vatAmount: exp.vatAmount ?? '',
        fromDate: exp.fromDate || exp.date || '',
        toDate: exp.toDate || '',
        recordedDate: exp.recordedDate || '',
        date: exp.date,
        paymentStatus: exp.paymentStatus,
        paymentMode: exp.paymentMode,
        supplierName: exp.supplierName || '',
        dueDate: exp.dueDate || '',
        staffMemberId: exp.staffMemberId ?? exp.staffMember?.id ?? '',
        salaryMonth: exp.salaryMonth || '',
        description: exp.description || '',
        cgst: exp.cgst ?? '',
        sgst: exp.sgst ?? '',
        igst: exp.igst ?? '',
      });
      setEditFormOpen(true);
    } catch (err: unknown) {
      console.error('Error loading expense:', err);
      toast.error('Failed to load payable');
    }
  };

  const handleSaveEditPayable = async (data: ExpenseFormData) => {
    if (!data.id) return;
    await financeApi.updateExpense(data.id, {
      categoryId: data.categoryId,
      amount: Number(data.amount),
      vatAmount: data.vatAmount === '' ? null : Number(data.vatAmount),
      date: data.fromDate,
      fromDate: data.fromDate,
      toDate: data.toDate || null,
      recordedDate: data.recordedDate || null,
      paymentStatus: data.paymentStatus,
      paymentMode: data.paymentMode,
      supplierName: data.supplierName || null,
      dueDate: data.dueDate || null,
      staffMemberId: data.staffMemberId || null,
      salaryMonth: data.salaryMonth || null,
      description: data.description || null,
      cgst: data.cgst === '' ? null : Number(data.cgst),
      sgst: data.sgst === '' ? null : Number(data.sgst),
      igst: data.igst === '' ? null : Number(data.igst),
    });
    toast.success('Payable updated');
    setEditFormOpen(false);
    setEditingExpense(null);
    await fetchPayables();
  };

  // --- Helpers ---

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '--';
    try {
      return new Date(dateStr).toLocaleDateString(CURRENCY.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Display "from → to" if both present (and differ); otherwise just from/fallback.
  // Caller usually wraps with a Tooltip surfacing both dates explicitly.
  const formatDateRange = (from?: string | null, to?: string | null, fallback?: string | null): string => {
    const start = from || fallback || null;
    if (!start) return '--';
    if (to && to !== start) return `${formatDate(start)} → ${formatDate(to)}`;
    return formatDate(start);
  };

  const dateRangeTooltip = (from?: string | null, to?: string | null, fallback?: string | null): string => {
    const start = from || fallback || null;
    if (!start) return '';
    if (to && to !== start) return `From ${formatDate(start)} to ${formatDate(to)}`;
    return formatDate(start);
  };

  const isOverdue = (dueDate: string | null): boolean => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date(todayLocalIso());
  };

  // --- Shared styles ---

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components?.input?.focus || theme.primary },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
  };

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: theme.components?.input?.background || '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.border || '#DDD8D2' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.focus || theme.primary },
    '& .MuiSelect-select': { color: theme.text },
  };

  // --- Skeleton ---

  const renderSkeletonCards = () =>
    Array.from({ length: 3 }).map((_, i) => (
      <CardContainer key={i} disableHover sx={{ p: 2, mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Skeleton variant="rounded" width={120} height={24} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
          <Skeleton variant="rounded" width={80} height={28} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="text" width={80} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
          <Skeleton variant="text" width={80} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
        </Box>
      </CardContainer>
    ));

  const renderSkeletonTable = () =>
    Array.from({ length: 4 }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: 5 }).map((__, j) => (
          <TableCell key={j}>
            <Skeleton variant="text" sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
          </TableCell>
        ))}
      </TableRow>
    ));

  // --- Supplier Card (Mobile) ---

  const renderSupplierCard = (supplier: SupplierPayable) => {
    const isExpanded = expandedSupplier === supplier.supplierName;
    return (
      <CardContainer
        key={supplier.supplierName}
        disableHover
        sx={{ mb: 1.5 }}
      >
        {/* Header */}
        <Box
          onClick={() => setExpandedSupplier(isExpanded ? null : supplier.supplierName)}
          sx={{
            p: 2,
            cursor: 'pointer',
            '&:active': { backgroundColor: `${theme.primary}08` },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: theme.text, fontSize: '0.95rem' }} noWrap>
                {supplier.supplierName}
              </Typography>
              {supplier.outstanding <= 0 ? (
                <StatusChip
                  status="Paid"
                  variant="success"
                  size="small"
                  sx={{ mt: 0.5, fontSize: '0.65rem', height: 22 }}
                />
              ) : supplier.overdue ? (
                <StatusChip
                  status="Overdue"
                  variant="error"
                  icon={<ExclamationTriangleIcon style={{ width: 12, height: 12 }} />}
                  size="small"
                  sx={{ mt: 0.5, fontSize: '0.65rem', height: 22 }}
                />
              ) : null}
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
              <Typography sx={{ fontWeight: 700, color: theme.danger || '#D99982', fontSize: '1.1rem', lineHeight: 1.2 }}>
                {formatCurrency(supplier.outstanding, { showDecimals: false })}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.65rem' }}>
                outstanding
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.6rem', textTransform: 'uppercase' }}>
                  Total
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text, fontSize: '0.813rem' }}>
                  {formatCurrency(supplier.totalExpenses, { showDecimals: false })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.6rem', textTransform: 'uppercase' }}>
                  Paid
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.success || '#4CAF50', fontSize: '0.813rem' }}>
                  {formatCurrency(supplier.totalPaid, { showDecimals: false })}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {view !== 'completed' && supplier.outstanding > 0 && (
                <Chip
                  icon={<CurrencyDollarIcon style={{ width: 12, height: 12 }} />}
                  label="Pay"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenBulkPay(supplier.supplierName);
                  }}
                  sx={{
                    fontSize: '0.65rem',
                    height: 24,
                    fontWeight: 700,
                    cursor: 'pointer',
                    backgroundColor: `${theme.success || '#4CAF50'}1f`,
                    color: theme.success || '#4CAF50',
                    border: `1px solid ${theme.success || '#4CAF50'}55`,
                    '& .MuiChip-icon': { color: theme.success || '#4CAF50', marginLeft: '6px' },
                    '&:hover': { backgroundColor: `${theme.success || '#4CAF50'}33` },
                  }}
                  data-testid={`supplier-pay-mobile-${supplier.supplierName}`}
                />
              )}
              <Chip
                icon={<ClockIcon style={{ width: 12, height: 12 }} />}
                label="History"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setHistorySupplier(supplier.supplierName);
                }}
                sx={{
                  fontSize: '0.65rem',
                  height: 24,
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: `${theme.primary}15`,
                  color: theme.primary,
                  border: `1px solid ${theme.primary}44`,
                  '& .MuiChip-icon': { color: theme.primary, marginLeft: '6px' },
                  '&:hover': { backgroundColor: `${theme.primary}25` },
                }}
              />
              {isExpanded ? (
                <ChevronUpIcon style={{ width: 18, height: 18, color: theme.gray?.[500] || '#666' }} />
              ) : (
                <ChevronDownIcon style={{ width: 18, height: 18, color: theme.gray?.[500] || '#666' }} />
              )}
            </Box>
          </Box>
        </Box>

        {/* Expanded expenses */}
        <Collapse in={isExpanded}>
          <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${theme.components?.card?.border || '#E0E0E0'}` }}>
            {supplier.expenses.map((exp) => (
              <Box
                key={exp.id}
                sx={{
                  py: 1.5,
                  borderBottom: `1px solid ${theme.components?.card?.border || '#E0E0E0'}22`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: theme.text, fontWeight: 500, fontSize: '0.813rem' }}>
                      {exp.categoryName}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Tooltip title={dateRangeTooltip(exp.fromDate, exp.toDate, exp.date)} arrow placement="top">
                        <Typography
                          variant="caption"
                          sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.7rem', lineHeight: 1.3 }}
                        >
                          {formatDateRange(exp.fromDate, exp.toDate, exp.date)}
                        </Typography>
                      </Tooltip>
                      {exp.dueDate && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: isOverdue(exp.dueDate) ? (theme.danger || '#D99982') : (theme.gray?.[500] || '#666'),
                            fontWeight: isOverdue(exp.dueDate) ? 600 : 400,
                            fontSize: '0.65rem',
                            lineHeight: 1.3,
                          }}
                        >
                          Due: {formatDate(exp.dueDate)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '0.95rem', flexShrink: 0 }}>
                    {formatCurrency(exp.amount, { showDecimals: false })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <StatusChip
                      status={
                        exp.paymentStatus === 'PAID'
                          ? 'Paid'
                          : exp.paymentStatus === 'PARTIALLY_PAID'
                            ? `Partial (${formatCurrency(exp.amountPaid, { showDecimals: false })})`
                            : 'Unpaid'
                      }
                      variant={
                        exp.paymentStatus === 'PAID'
                          ? 'success'
                          : exp.paymentStatus === 'PARTIALLY_PAID'
                            ? 'warning'
                            : 'error'
                      }
                      size="small"
                      sx={{ fontSize: '0.65rem', height: 22 }}
                    />
                    {periodWindow && !expenseFullyInside(exp, periodWindow.from, periodWindow.to) && (
                      <StatusChip
                        status="Partial period"
                        variant="warning"
                        size="small"
                        sx={{ fontSize: '0.6rem', height: 20 }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                    <Chip
                      icon={<PencilSquareIcon style={{ width: 12, height: 12 }} />}
                      label="Edit"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditPayable(exp.id);
                      }}
                      sx={{
                        fontSize: '0.65rem',
                        height: 28,
                        fontWeight: 600,
                        cursor: 'pointer',
                        backgroundColor: `${theme.primary}15`,
                        color: theme.primary,
                        border: `1px solid ${theme.primary}44`,
                        '& .MuiChip-icon': { color: theme.primary, marginLeft: '6px' },
                        '&:hover': { backgroundColor: `${theme.primary}25` },
                      }}
                    />
                    {view !== 'completed' && (
                      <SuccessButton
                        size="small"
                        icon={<CurrencyDollarIcon style={{ width: 14, height: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenPayModal(exp, supplier.supplierName);
                        }}
                        sx={{ fontSize: '0.7rem', minHeight: 28, px: 1.25 }}
                      >
                        Pay
                      </SuccessButton>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      </CardContainer>
    );
  };

  // --- Render ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh', pb: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', sm: 3 } }}>
      <FinanceTabBar />

      {/* Header — confident-shell hero band */}
      <PageHeroBand
        eyebrow="Finance · Payables"
        title="Supplier Payables"
        actions={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              // Outlined (secondary) buttons wash over the dark band; filled
              // CTAs (Add Payable / Record Payment) keep their fills.
              '& .MuiButton-outlined': {
                backgroundColor: 'color-mix(in srgb, var(--band-text) 10%, transparent)',
                color: 'var(--band-text)',
                borderColor: 'color-mix(in srgb, var(--band-text) 25%, transparent)',
                '&:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--band-text) 18%, transparent)',
                  borderColor: 'color-mix(in srgb, var(--band-text) 40%, transparent)',
                },
              },
            }}
          >
            {view === 'outstanding' && payablesData.suppliers.length > 0 && (
              <SuccessButton
                size="small"
                icon={<CurrencyDollarIcon style={{ width: 16, height: 16 }} />}
                onClick={() => handleOpenBulkPay()}
                data-testid="bulk-record-payment-button"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.813rem' }, minHeight: { xs: 36, sm: 40 }, px: { xs: 1.25, sm: 2 } }}
              >
                {isMobile ? 'Pay' : 'Record Payment'}
              </SuccessButton>
            )}
            <PrimaryButton
              size="small"
              startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
              onClick={handleOpenAddPayable}
              data-testid="add-payable-button"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.813rem' }, minHeight: { xs: 36, sm: 40 }, px: { xs: 1.25, sm: 2 } }}
            >
              {isMobile ? 'Add' : 'Add Payable'}
            </PrimaryButton>
            {isMobile ? (
              <AppIconButton
                icon={<ArrowPathIcon style={{ width: 16, height: 16 }} className={refreshing ? 'animate-spin' : ''} />}
                tooltip="Refresh"
                variant="secondary"
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="refresh-button"
              />
            ) : (
              <SecondaryButton
                startIcon={<ArrowPathIcon style={{ width: 16, height: 16 }} className={refreshing ? 'animate-spin' : ''} />}
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
                sx={{ fontSize: '0.813rem', minHeight: 40, flexShrink: 0 }}
              >
                Refresh
              </SecondaryButton>
            )}
          </Box>
        }
      />

      {/* Summary Bar — completed view repurposes cards to Total Paid + Supplier count */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
        <CardContainer disableHover sx={{ flex: 1, p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
            {view === 'completed' ? 'Total Paid' : 'Total Outstanding'}
          </Typography>
          {loading ? (
            <Skeleton variant="rounded" width={100} height={28} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', mt: 0.5 }} />
          ) : view === 'completed' ? (
            <Typography sx={{ fontWeight: 800, color: theme.success || '#4CAF50', fontSize: { xs: '1.1rem', sm: '1.35rem' }, lineHeight: 1.2, mt: 0.25 }}>
              {formatCurrency(filteredSuppliers.reduce((s, x) => s + (x.totalPaid || 0), 0), { showDecimals: false })}
            </Typography>
          ) : (
            <Typography sx={{ fontWeight: 800, color: theme.danger || '#D99982', fontSize: { xs: '1.1rem', sm: '1.35rem' }, lineHeight: 1.2, mt: 0.25 }}>
              {formatCurrency(filtersActive ? filteredTotalOutstanding : payablesData.totalOutstanding, { showDecimals: false })}
            </Typography>
          )}
          {filtersActive && !loading && (
            <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.6rem', mt: 0.25, display: 'block' }}>
              Filtered view
            </Typography>
          )}
        </CardContainer>
        <CardContainer disableHover sx={{ flex: 0.7, p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {view === 'completed' ? null : (
              <ExclamationTriangleIcon style={{ width: 12, height: 12, color: theme.warning || '#FFC107' }} />
            )}
            <Typography variant="caption" sx={{ color: view === 'completed' ? (theme.gray?.[500] || '#666') : (theme.warning || '#FFC107'), fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
              {view === 'completed' ? 'Suppliers' : 'Overdue'}
            </Typography>
          </Box>
          {loading ? (
            <Skeleton variant="rounded" width={80} height={28} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', mt: 0.5 }} />
          ) : view === 'completed' ? (
            <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: { xs: '1.1rem', sm: '1.35rem' }, lineHeight: 1.2, mt: 0.25 }}>
              {filteredSuppliers.length}
            </Typography>
          ) : (
            <Typography sx={{ fontWeight: 700, color: theme.warning || '#FFC107', fontSize: { xs: '1.1rem', sm: '1.35rem' }, lineHeight: 1.2, mt: 0.25 }}>
              {formatCurrency(filtersActive ? filteredOverdueAmount : payablesData.overdueAmount, { showDecimals: false })}
            </Typography>
          )}
        </CardContainer>
      </Box>

      {/* View toggle: Outstanding | Completed */}
      <Box sx={{ mb: 1.5, display: 'flex', gap: 0.75 }}>
        {[
          { key: 'outstanding' as PayablesView, label: 'Outstanding' },
          { key: 'completed' as PayablesView, label: 'Completed' },
        ].map((opt) => {
          const isActive = view === opt.key;
          return (
            <Chip
              key={opt.key}
              label={opt.label}
              onClick={() => { setView(opt.key); setExpandedSupplier(null); }}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 32,
                px: 1,
                borderRadius: '10px',
                cursor: 'pointer',
                backgroundColor: isActive ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                color: isActive ? theme.primary : theme.gray?.[500] || '#666',
                border: isActive ? `1.5px solid ${theme.primary}` : '1px solid transparent',
                '&:hover': { backgroundColor: isActive ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
              }}
            />
          );
        })}
      </Box>

      {/* Compact filter row: Search + Filters chip + Insights chip */}
      {!loading && payablesData.suppliers.length > 0 && (
        <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search supplier…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ ...inputSx, flex: { xs: '1 1 100%', sm: '1 1 240px' }, minWidth: { xs: '100%', sm: 240 } }}
            inputProps={{ 'data-testid': 'payables-search' }}
          />
          <Chip
            icon={<AdjustmentsHorizontalIcon style={{ width: 14, height: 14 }} />}
            deleteIcon={filtersExpanded ? <ChevronUpIcon style={{ width: 14, height: 14 }} /> : <ChevronDownIcon style={{ width: 14, height: 14 }} />}
            onDelete={() => setFiltersExpanded(!filtersExpanded)}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            data-testid="payables-toggle-filters"
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                Filters
                {activeFilterCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 18,
                      height: 18,
                      px: 0.5,
                      borderRadius: '9px',
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      backgroundColor: theme.primary,
                      color: 'var(--text-inverse)',
                    }}
                  >
                    {activeFilterCount}
                  </Box>
                )}
              </Box>
            }
            sx={{
              fontSize: '0.75rem',
              height: 32,
              fontWeight: 700,
              cursor: 'pointer',
              px: 0.5,
              backgroundColor: activeFilterCount > 0 ? `${theme.primary}1f` : (theme.gray?.[100] || '#F0F0F0'),
              color: activeFilterCount > 0 ? theme.primary : (theme.gray?.[600] || '#555'),
              border: activeFilterCount > 0 ? `1.5px solid ${theme.primary}66` : `1px solid ${theme.gray?.[300] || '#D0D0D0'}`,
              '& .MuiChip-icon': { color: activeFilterCount > 0 ? theme.primary : (theme.gray?.[600] || '#555'), marginLeft: '6px' },
              '& .MuiChip-deleteIcon': { color: activeFilterCount > 0 ? theme.primary : (theme.gray?.[600] || '#555'), margin: '0 4px 0 -4px', '&:hover': { color: theme.primary } },
              '&:hover': { backgroundColor: activeFilterCount > 0 ? `${theme.primary}33` : (theme.gray?.[200] || '#E0E0E0') },
            }}
          />
          {/* Insights chip stays here so user sees both controls on one row */}
          <Tooltip
            arrow
            placement="top"
            title={chartsVisible ? 'Collapse the insights strip' : 'Aging breakdown · Top suppliers (Paid vs Outstanding) · 30/60/90 cash forecast'}
          >
            <Chip
              icon={
                chartsVisible
                  ? <EyeSlashIcon style={{ width: 14, height: 14 }} />
                  : <ChartBarIcon style={{ width: 14, height: 14 }} />
              }
              label={chartsVisible ? 'Hide insights' : 'Show insights'}
              onClick={() => setChartsVisible(!chartsVisible)}
              data-testid="payables-toggle-charts"
              sx={{
                fontSize: '0.75rem',
                height: 32,
                fontWeight: 700,
                cursor: 'pointer',
                px: 0.5,
                backgroundColor: chartsVisible ? (theme.gray?.[100] || '#F0F0F0') : `${theme.primary}1f`,
                color: chartsVisible ? (theme.gray?.[600] || '#555') : theme.primary,
                border: chartsVisible ? `1px solid ${theme.gray?.[300] || '#D0D0D0'}` : `1.5px solid ${theme.primary}66`,
                '& .MuiChip-icon': { color: chartsVisible ? (theme.gray?.[600] || '#555') : theme.primary, marginLeft: '6px' },
                '&:hover': { backgroundColor: chartsVisible ? (theme.gray?.[200] || '#E0E0E0') : `${theme.primary}33` },
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Expanded filter panel: Aging + Categories + Period (Custom dates inline) */}
      {!loading && payablesData.suppliers.length > 0 && (
        <Collapse in={filtersExpanded} timeout={200} unmountOnExit>
          <Box
            sx={{
              mb: 1.5,
              p: { xs: 1.25, sm: 1.5 },
              backgroundColor: `${theme.primary}06`,
              border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
              borderRadius: '12px',
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 1fr) minmax(180px, 1.6fr) minmax(160px, 1fr)' },
              alignItems: 'stretch',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <InputLabel sx={{ color: theme.gray?.[500] || '#666' }}>Aging</InputLabel>
              <Select
                value={aging}
                label="Aging"
                onChange={(e) => setAging(e.target.value as 'all' | AgingBucket)}
                sx={selectSx}
                data-testid="payables-aging-select"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="current">Current</MenuItem>
                <MenuItem value="1_30">1–30 days</MenuItem>
                <MenuItem value="31_60">31–60 days</MenuItem>
                <MenuItem value="60_plus">60+ days</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <InputLabel shrink sx={{ color: theme.gray?.[500] || '#666' }}>Categories</InputLabel>
              <Select
                multiple
                displayEmpty
                notched
                value={selectedCategories}
                label="Categories"
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedCategories(typeof v === 'string' ? v.split(',') : v);
                }}
                renderValue={(vals) =>
                  (vals as string[]).length === 0
                    ? 'All'
                    : (vals as string[]).length === 1
                      ? (vals as string[])[0]
                      : `${(vals as string[]).length} selected`
                }
                sx={selectSx}
                data-testid="payables-categories-select"
              >
                {expenseCategories.map((c) => (
                  <MenuItem key={c.id} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <InputLabel sx={{ color: theme.gray?.[500] || '#666' }}>Period</InputLabel>
              <Select
                value={period}
                label="Period"
                onChange={(e) => setPeriod(e.target.value as PeriodPreset)}
                sx={selectSx}
                data-testid="payables-period-select"
              >
                <MenuItem value="all">All time</MenuItem>
                <MenuItem value="this_month">This month</MenuItem>
                <MenuItem value="last_month">Last month</MenuItem>
                <MenuItem value="this_quarter">This quarter</MenuItem>
                <MenuItem value="custom">Custom…</MenuItem>
              </Select>
            </FormControl>
            {period === 'custom' && (
              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, display: 'flex' }}>
                <FinanceDateRange
                  from={customFrom}
                  to={customTo}
                  onFromChange={(v) => setCustomRange(v, customTo)}
                  onToChange={(v) => setCustomRange(customFrom, v)}
                  showLabels
                />
              </Box>
            )}
          </Box>
        </Collapse>
      )}

      {/* Active filter pills + Clear all */}
      {!loading && filtersActive && (
        <Box sx={{ mb: 1.5, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {q.trim() !== '' && (
            <Chip
              size="small"
              label={`Search: "${q}"`}
              onDelete={() => setQ('')}
              sx={{ fontSize: '0.65rem', height: 22, backgroundColor: `${theme.primary}15`, color: theme.primary }}
            />
          )}
          {aging !== 'all' && (
            <Chip
              size="small"
              label={`Aging: ${aging === 'current' ? 'Current' : aging === '1_30' ? '1–30d' : aging === '31_60' ? '31–60d' : '60+d'}`}
              onDelete={() => setAging('all')}
              sx={{ fontSize: '0.65rem', height: 22, backgroundColor: `${theme.primary}15`, color: theme.primary }}
            />
          )}
          {selectedCategories.length > 0 && (
            <Chip
              size="small"
              label={`Categories: ${selectedCategories.length}`}
              onDelete={() => setSelectedCategories([])}
              sx={{ fontSize: '0.65rem', height: 22, backgroundColor: `${theme.primary}15`, color: theme.primary }}
            />
          )}
          {period !== 'all' && (
            <Chip
              size="small"
              label={`Period: ${period === 'this_month' ? 'This month' : period === 'last_month' ? 'Last month' : period === 'this_quarter' ? 'This quarter' : 'Custom'}`}
              onDelete={() => setPeriod('all')}
              sx={{ fontSize: '0.65rem', height: 22, backgroundColor: `${theme.primary}15`, color: theme.primary }}
            />
          )}
          <Typography
            component="button"
            type="button"
            onClick={clearAllFilters}
            sx={{
              fontSize: '0.7rem',
              color: theme.primary,
              fontWeight: 600,
              ml: 0.5,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0,
              textDecoration: 'underline',
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      {/* Filter-aware totals over the currently listed suppliers */}
      {!loading && filteredSuppliers.length > 0 && (
        <FilteredTotals
          data-testid="payables-filtered-totals"
          items={[
            {
              label: 'Outstanding',
              value: formatCurrency(filteredTotalOutstanding, { showDecimals: false }),
            },
            {
              label: 'Paid',
              value: formatCurrency(
                filteredSuppliers.reduce((sum, s) => sum + (s.totalPaid || 0), 0),
                { showDecimals: false },
              ),
            },
            { label: 'Suppliers', value: String(filteredSuppliers.length) },
          ]}
        />
      )}

      {!loading && chartsVisible && filteredSuppliers.length > 0 && (
        <PayablesCharts
          suppliers={filteredSuppliers}
          periodWindow={periodWindow}
          onAgingClick={(b) => setAging(b)}
          onSupplierClick={(name) => setQ(name)}
        />
      )}

      {/* Sort control */}
      {!loading && sortedSuppliers.length > 0 && view === 'outstanding' && (
        <Box sx={{ mb: 1.5, display: 'flex', gap: 0.75 }}>
          {[
            { key: 'outstanding_desc' as SortMode, label: 'Highest Outstanding' },
            { key: 'due_asc' as SortMode, label: 'Overdue First' },
          ].map((opt) => {
            const isActive = sortMode === opt.key;
            return (
              <Chip
                key={opt.key}
                label={opt.label}
                onClick={() => setSortMode(opt.key)}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 28,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: isActive ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                  color: isActive ? theme.primary : theme.gray?.[500] || '#666',
                  border: isActive ? `1.5px solid ${theme.primary}` : '1px solid transparent',
                  '&:hover': { backgroundColor: isActive ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Supplier List */}
      {loading ? (
        isMobile ? renderSkeletonCards() : (
          <CardContainer disableHover>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Supplier', 'Total', 'Paid', 'Outstanding', 'Status'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 600, color: theme.text, borderBottom: `2px solid ${theme.components?.card?.border || '#E0E0E0'}`, backgroundColor: theme.components?.table?.header || theme.background, fontSize: '0.813rem' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>{renderSkeletonTable()}</TableBody>
              </Table>
            </TableContainer>
          </CardContainer>
        )
      ) : filtersActive && sortedSuppliers.length === 0 ? (
        <EmptyState
          icon={<CurrencyDollarIcon style={{ width: 48, height: 48, color: theme.gray?.[400] || '#999' }} />}
          title="No suppliers match these filters"
          subtitle="Try widening the period, clearing the category, or adjusting the search."
          actionButton={
            <SecondaryButton size="small" onClick={clearAllFilters} sx={{ fontSize: '0.813rem', minHeight: 36 }}>
              Clear filters
            </SecondaryButton>
          }
        />
      ) : payablesData.suppliers.length === 0 ? (
        <EmptyState
          icon={<CurrencyDollarIcon style={{ width: 48, height: 48, color: theme.gray?.[400] || '#999' }} />}
          title={view === 'completed' ? 'No completed payables yet' : 'No outstanding payables'}
          subtitle={view === 'completed' ? 'Fully paid payables will appear here for history tracking.' : 'Add payables manually or import from a bank statement'}
          actionButton={view === 'completed' ? undefined : (
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <PrimaryButton
                size="small"
                startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
                onClick={handleOpenAddPayable}
                sx={{ fontSize: '0.813rem', minHeight: 36 }}
              >
                Add Payable
              </PrimaryButton>
              <SecondaryButton
                size="small"
                startIcon={<ArrowUpTrayIcon style={{ width: 16, height: 16 }} />}
                onClick={() => navigate('/finance/import')}
                sx={{ fontSize: '0.813rem', minHeight: 36 }}
                data-testid="import-from-bank-button"
              >
                Import from Bank
              </SecondaryButton>
            </Box>
          )}
        />
      ) : isMobile ? (
        /* Mobile: Card layout */
        <Box>{sortedSuppliers.map(renderSupplierCard)}</Box>
      ) : (
        /* Desktop: Table layout */
        <CardContainer disableHover>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Supplier', 'Total Expenses', 'Paid', 'Outstanding', 'Status', ''].map((h) => (
                    <TableCell
                      key={h}
                      align={h === 'Total Expenses' || h === 'Paid' || h === 'Outstanding' ? 'right' : 'left'}
                      sx={{
                        fontWeight: 600,
                        color: theme.text,
                        borderBottom: `2px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                        backgroundColor: theme.components?.table?.header || theme.background,
                        fontSize: '0.813rem',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSuppliers.map((supplier) => {
                  const isExpanded = expandedSupplier === supplier.supplierName;
                  return (
                    <React.Fragment key={supplier.supplierName}>
                      <TableRow
                        hover
                        onClick={() => setExpandedSupplier(isExpanded ? null : supplier.supplierName)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: `${theme.primary}08` } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem' }}>
                              {supplier.supplierName}
                            </Typography>
                            {supplier.overdue && (
                              <StatusChip
                                status="Overdue"
                                variant="error"
                                icon={<ExclamationTriangleIcon style={{ width: 12, height: 12 }} />}
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ color: theme.text, fontSize: '0.875rem', fontWeight: 600 }}>
                          {formatCurrency(supplier.totalExpenses, { showDecimals: false })}
                        </TableCell>
                        <TableCell align="right" sx={{ color: theme.success || '#4CAF50', fontSize: '0.875rem', fontWeight: 600 }}>
                          {formatCurrency(supplier.totalPaid, { showDecimals: false })}
                        </TableCell>
                        <TableCell align="right" sx={{ color: theme.danger || '#D99982', fontSize: '0.95rem', fontWeight: 700 }}>
                          {formatCurrency(supplier.outstanding, { showDecimals: false })}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            status={
                              supplier.outstanding <= 0
                                ? 'Paid'
                                : supplier.overdue
                                  ? 'Overdue'
                                  : 'Pending'
                            }
                            variant={
                              supplier.outstanding <= 0
                                ? 'success'
                                : supplier.overdue
                                  ? 'error'
                                  : 'warning'
                            }
                            size="small"
                            sx={{ fontSize: '0.65rem', height: 22 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {view !== 'completed' && supplier.outstanding > 0 && (
                              <SuccessButton
                                size="small"
                                icon={<CurrencyDollarIcon style={{ width: 12, height: 12 }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenBulkPay(supplier.supplierName);
                                }}
                                sx={{ fontSize: '0.65rem', minHeight: 24, px: 1 }}
                                data-testid={`supplier-pay-${supplier.supplierName}`}
                              >
                                Pay
                              </SuccessButton>
                            )}
                            <Chip
                              icon={<ClockIcon style={{ width: 12, height: 12 }} />}
                              label="History"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setHistorySupplier(supplier.supplierName);
                              }}
                              sx={{
                                fontSize: '0.65rem',
                                height: 24,
                                fontWeight: 600,
                                cursor: 'pointer',
                                backgroundColor: `${theme.primary}15`,
                                color: theme.primary,
                                border: `1px solid ${theme.primary}44`,
                                '& .MuiChip-icon': { color: theme.primary, marginLeft: '6px' },
                                '&:hover': { backgroundColor: `${theme.primary}25` },
                              }}
                            />
                            {isExpanded ? (
                              <ChevronUpIcon style={{ width: 18, height: 18, color: theme.gray?.[500] || '#666' }} />
                            ) : (
                              <ChevronDownIcon style={{ width: 18, height: 18, color: theme.gray?.[500] || '#666' }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      {/* Expanded expense rows */}
                      {isExpanded && supplier.expenses.map((exp) => (
                        <TableRow key={exp.id} sx={{ backgroundColor: `${theme.primary}04` }}>
                          <TableCell sx={{ pl: 5, color: theme.gray?.[500] || '#666', fontSize: '0.813rem' }}>
                            {exp.categoryName}
                          </TableCell>
                          <TableCell align="right" sx={{ color: theme.text, fontSize: '0.813rem' }}>
                            <Tooltip title={dateRangeTooltip(exp.fromDate, exp.toDate, exp.date)} arrow placement="top">
                              <span>{formatDateRange(exp.fromDate, exp.toDate, exp.date)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ color: theme.text, fontSize: '0.875rem', fontWeight: 600 }}>
                            {formatCurrency(exp.amount, { showDecimals: false })}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <StatusChip
                                status={
                                  exp.paymentStatus === 'PAID'
                                    ? 'Paid'
                                    : exp.paymentStatus === 'PARTIALLY_PAID'
                                      ? `Partial (${formatCurrency(exp.amountPaid, { showDecimals: false })})`
                                      : 'Unpaid'
                                }
                                variant={
                                  exp.paymentStatus === 'PAID'
                                    ? 'success'
                                    : exp.paymentStatus === 'PARTIALLY_PAID'
                                      ? 'warning'
                                      : 'error'
                                }
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 20 }}
                              />
                              {exp.dueDate && isOverdue(exp.dueDate) && (
                                <Typography variant="caption" sx={{ color: theme.danger || '#D99982', fontWeight: 600, fontSize: '0.65rem' }}>
                                  Due: {formatDate(exp.dueDate)}
                                </Typography>
                              )}
                              {periodWindow && !expenseFullyInside(exp, periodWindow.from, periodWindow.to) && (
                                <StatusChip
                                  status="Partial period"
                                  variant="warning"
                                  size="small"
                                  sx={{ fontSize: '0.6rem', height: 20 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell colSpan={2} align="right">
                            <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end', alignItems: 'center' }}>
                              <Chip
                                icon={<PencilSquareIcon style={{ width: 12, height: 12 }} />}
                                label="Edit"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEditPayable(exp.id);
                                }}
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 28,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  backgroundColor: `${theme.primary}15`,
                                  color: theme.primary,
                                  border: `1px solid ${theme.primary}44`,
                                  '& .MuiChip-icon': { color: theme.primary, marginLeft: '6px' },
                                  '&:hover': { backgroundColor: `${theme.primary}25` },
                                }}
                              />
                              {view !== 'completed' && (
                                <SuccessButton
                                  size="small"
                                  icon={<CurrencyDollarIcon style={{ width: 14, height: 14 }} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPayModal(exp, supplier.supplierName);
                                  }}
                                  sx={{ fontSize: '0.7rem', minHeight: 28 }}
                                >
                                  Record Payment
                                </SuccessButton>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContainer>
      )}

      {/* Record Payment Modal */}
      <Modal
        isOpen={payModalOpen}
        onClose={handleClosePayModal}
        title="Record Payment"
        maxWidth="sm"
        footer={
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
            <SecondaryButton
              onClick={handleClosePayModal}
              disabled={saving}
              fullWidth
            >
              Cancel
            </SecondaryButton>
            <SuccessButton
              onClick={handleRecordPayment}
              disabled={saving || !payAmount || Number(payAmount) <= 0}
              loading={saving}
              fullWidth
            >
              Record Payment
            </SuccessButton>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: { xs: 0, sm: 1 } }}>
          {payingExpense && (
            <CardContainer disableHover sx={{ p: 1.5, backgroundColor: `${theme.primary}06` }}>
              <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, fontSize: '0.75rem' }}>
                {payingSupplier} &mdash; {formatDate(payingExpense.date)}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1.1rem', mt: 0.25 }}>
                {formatCurrency(payingExpense.amount, { showDecimals: false })}
                {(payingExpense.amountPaid || 0) > 0 && (
                  <Typography component="span" sx={{ fontSize: '0.813rem', color: theme.success || '#4CAF50', ml: 1 }}>
                    ({formatCurrency(payingExpense.amountPaid, { showDecimals: false })} paid)
                  </Typography>
                )}
              </Typography>
            </CardContainer>
          )}

          <TextField
            label="Payment Amount"
            type="number"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600 }}>
                    {CURRENCY.symbol}
                  </Typography>
                </InputAdornment>
              ),
              inputProps: { min: 0, step: 1, style: { fontSize: '1.25rem', fontWeight: 700 } },
            }}
            sx={inputSx}
            fullWidth
          />

          <PaymentModeSelect
            value={payMode}
            onChange={(v) => setPayMode(v)}
            label="Payment Mode"
            fullWidth
          />

          <TextField
            label="Date"
            type="date"
            value={payDate}
            onChange={(e) => setPayDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
            fullWidth
          />

          <TextField
            label="Notes (optional)"
            multiline
            minRows={2}
            maxRows={3}
            value={payNotes}
            onChange={(e) => setPayNotes(e.target.value)}
            placeholder="e.g., Cheque #12345"
            sx={inputSx}
            fullWidth
          />

        </Box>
      </Modal>

      {/* Add Payable Modal */}
      <Modal
        isOpen={addPayableOpen}
        onClose={() => setAddPayableOpen(false)}
        title="Add Payable"
        maxWidth="sm"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: { xs: 0, sm: 1 } }}>
          {/* Supplier Name - Autocomplete freeSolo */}
          <Autocomplete
            freeSolo
            options={supplierOptions}
            value={addPayable.supplierName}
            onInputChange={(_, val) => setAddPayable((prev) => ({ ...prev, supplierName: val }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supplier Name *"
                placeholder="e.g. Metro Cash & Carry"
                sx={inputSx}
              />
            )}
            sx={{ '& .MuiAutocomplete-popupIndicator': { color: theme.gray?.[400] || '#999' } }}
          />

          {/* Amount */}
          <TextField
            label="Amount *"
            type="number"
            value={addPayable.amount}
            onChange={(e) => setAddPayable((prev) => ({ ...prev, amount: e.target.value }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '1.1rem' }}>
                    {CURRENCY.symbol}
                  </Typography>
                </InputAdornment>
              ),
              inputProps: { min: 0, step: 1, style: { fontSize: '1.25rem', fontWeight: 700 } },
            }}
            sx={inputSx}
            fullWidth
          />

          {/* From / To Date (period covered) */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              label="From Date *"
              type="date"
              value={addPayable.fromDate}
              onChange={(e) => setAddPayable((prev) => ({ ...prev, fromDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
              fullWidth
            />
            <TextField
              label="To Date"
              type="date"
              value={addPayable.toDate}
              onChange={(e) => setAddPayable((prev) => ({ ...prev, toDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for single day"
              sx={inputSx}
              fullWidth
            />
          </Box>

          {/* Due Date */}
          <TextField
            label="Due Date *"
            type="date"
            value={addPayable.dueDate}
            onChange={(e) => setAddPayable((prev) => ({ ...prev, dueDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
            fullWidth
          />

          {/* Category */}
          <FormControl fullWidth>
            <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary } }}>
              Category *
            </InputLabel>
            <Select
              value={addPayable.categoryId}
              label="Category *"
              onChange={(e) => setAddPayable((prev) => ({ ...prev, categoryId: e.target.value as number }))}
              sx={selectSx}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {expenseCategories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Description */}
          <TextField
            label="Description (optional)"
            multiline
            minRows={2}
            maxRows={3}
            value={addPayable.description}
            onChange={(e) => setAddPayable((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="e.g. Monthly vegetables supply"
            sx={inputSx}
            fullWidth
          />

          {/* Actions — grid splits row evenly without overflow on mobile */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5,
              mt: 0.5,
            }}
          >
            <SecondaryButton
              onClick={() => setAddPayableOpen(false)}
              disabled={savingPayable}
              fullWidth
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSavePayable}
              disabled={savingPayable || !addPayable.supplierName.trim() || !addPayable.amount || !addPayable.fromDate || !addPayable.dueDate || !addPayable.categoryId}
              loading={savingPayable}
              fullWidth
            >
              Add Payable
            </PrimaryButton>
          </Box>
        </Box>
      </Modal>

      {/* Supplier Payment History Modal */}
      <SupplierHistoryModal
        isOpen={Boolean(historySupplier)}
        onClose={() => setHistorySupplier(null)}
        supplierName={historySupplier || ''}
        onPaymentChanged={fetchPayables}
      />

      {/* Bulk Record Payment Modal (page-level — auto-allocates oldest-first) */}
      <Modal
        isOpen={bulkPayOpen}
        onClose={handleCloseBulkPay}
        title="Record Payment"
        subtitle="Settles oldest payables first"
        maxWidth="sm"
        footer={
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
            <SecondaryButton
              onClick={handleCloseBulkPay}
              disabled={bulkSaving}
              fullWidth
            >
              Cancel
            </SecondaryButton>
            <SuccessButton
              onClick={handleBulkPaySubmit}
              disabled={bulkSaving || !bulkSupplier || !bulkAmount || Number(bulkAmount) <= 0}
              loading={bulkSaving}
              fullWidth
            >
              Record Payment
            </SuccessButton>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: { xs: 0, sm: 1 } }}>
          {/* Supplier — restricted to suppliers with outstanding payables */}
          <Autocomplete
            options={payablesData.suppliers.map((s) => s.supplierName)}
            value={bulkSupplier || null}
            onChange={(_, val) => setBulkSupplier(val || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supplier *"
                placeholder="Select supplier"
                sx={inputSx}
              />
            )}
            sx={{ '& .MuiAutocomplete-popupIndicator': { color: theme.gray?.[400] || '#999' } }}
            data-testid="bulk-pay-supplier"
          />

          {/* Outstanding hint for selected supplier */}
          {bulkSupplier && (
            <CardContainer disableHover sx={{ p: 1.5, backgroundColor: `${theme.primary}06` }}>
              <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total outstanding
              </Typography>
              <Typography sx={{ fontWeight: 700, color: theme.danger || '#D99982', fontSize: '1.1rem', lineHeight: 1.2, mt: 0.25 }}>
                {formatCurrency(payablesData.suppliers.find((s) => s.supplierName === bulkSupplier)?.outstanding || 0, { showDecimals: false })}
              </Typography>
            </CardContainer>
          )}

          <TextField
            label="Payment Amount *"
            type="number"
            value={bulkAmount}
            onChange={(e) => setBulkAmount(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600 }}>
                    {CURRENCY.symbol}
                  </Typography>
                </InputAdornment>
              ),
              inputProps: { min: 0, step: 1, style: { fontSize: '1.25rem', fontWeight: 700 } },
            }}
            sx={inputSx}
            fullWidth
            data-testid="bulk-pay-amount"
          />

          <PaymentModeSelect
            value={bulkMode}
            onChange={(v) => setBulkMode(v)}
            label="Payment Mode"
            fullWidth
          />

          <TextField
            label="Date"
            type="date"
            value={bulkDate}
            onChange={(e) => setBulkDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
            fullWidth
          />

          <TextField
            label="Notes (optional)"
            multiline
            minRows={2}
            maxRows={3}
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.target.value)}
            placeholder="e.g., Cheque #12345"
            sx={inputSx}
            fullWidth
          />

          {/* Allocation preview — shows which payables this amount will settle */}
          {bulkPreview && bulkPreview.allocations.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75, display: 'block' }}>
                Will settle (oldest first)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {bulkPreview.allocations.map(({ exp, applied, fullySettled }) => (
                  <Box
                    key={exp.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 1.25,
                      py: 0.75,
                      borderRadius: '8px',
                      backgroundColor: fullySettled ? `${theme.success || '#4CAF50'}11` : `${theme.warning || '#FFC107'}15`,
                      border: `1px solid ${fullySettled ? (theme.success || '#4CAF50') : (theme.warning || '#FFC107')}33`,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ color: theme.text, fontSize: '0.78rem', fontWeight: 600 }}>
                        {formatDateRange(exp.fromDate, exp.toDate, exp.date)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.68rem' }}>
                        owed {formatCurrency(exp.amount - exp.amountPaid, { showDecimals: false })}
                        {exp.dueDate && ` · due ${formatDate(exp.dueDate)}`}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: fullySettled ? (theme.success || '#4CAF50') : (theme.warning || '#FFC107') }}>
                        {formatCurrency(applied, { showDecimals: false })}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase' }}>
                        {fullySettled ? 'Settled' : 'Partial'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {bulkPreview.remaining > 0 && (
                  <Typography variant="caption" sx={{ color: theme.danger || '#D99982', fontSize: '0.7rem', mt: 0.5, fontWeight: 600 }}>
                    {formatCurrency(bulkPreview.remaining, { showDecimals: false })} exceeds outstanding — will not be recorded
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Edit Payable (reuses ExpenseForm) */}
      <ExpenseForm
        open={editFormOpen}
        onClose={() => { setEditFormOpen(false); setEditingExpense(null); }}
        onSave={handleSaveEditPayable}
        expense={editingExpense}
        categories={editFormCategories}
      />
    </Box>
  );
};

export default SupplierPayables;
